import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Observable, catchError, from, of, switchMap, zip } from "rxjs";
import { InspectionForOffline } from "../../_objects/inspection";
import { ImagesSortOrder, InspectionImage, InspectionImageForOffline, InspectionImageWithUploadUrl, InspectionImagesSortOrderForOffline, LocationOrder, ReadImageOption } from "../../_objects/inspection-image";
import { IndividualReportForOffline } from "../../_objects/inspection-report";
import { MeasurementModeName } from "../../_objects/measurement-mode-name";
import { DataSyncAlertDialogComponent } from "../../_offline/component/dialog/data-sync-alert-dialog/data-sync-alert-dialog.component";
import { ProgressDialogComponent } from "../../_offline/component/dialog/progress-dialog/progress-dialog.component";
import { ProgressDialogService } from "../../_offline/component/dialog/progress-dialog/progress-dialog.service";
import { DATA_SYNC_STEP } from "../../_offline/electron.constants";
import { IndividualReportSchema } from "../../_offline/models/individual-report.schema";
import { InspectionSchema, InspectionSchemaUpdate } from "../../_offline/models/inspection.schema";
import { InspectionImageBaseSchema, InspectionImageSchema } from "../../_offline/models/inspectionImage.schema";
import { OfflineDataSyncErrorObject, UploadDataType, UploadManagementSchema } from "../../_offline/models/upload-management.schema";
import { OfflineUtilsService } from "../../_offline/services/offline-utils.service";
import { USE_IMAGE_TYPE } from "../../app.constants";
import { UtilsService } from "../_utils.service";
import { BlobClient } from "../blob.service";
import { CommonSnackBarService } from "../common-snack-bar.service";
import { IndividualReportDbService } from "../database/individual-report-db.service";
import { InspectionDbService } from "../database/inspection-db.service";
import { InspectionImageDbService } from "../database/inspection-image-db.service";
import { UploadManagementDbService } from "../database/upload-management-db.service";
import { FileSystemAccessService } from "../file-system-access.service";
import { OfflineManagementService } from "../offline-management.service";
import { LoginUserOfflineService } from "../offline/login-user-offline.service";
import { InspectionImageOnlineService } from "../online/inspection-image-online.service";
import { SpinnerService } from "../spinner.service";

@Injectable({
  providedIn: 'root'
})
export class PushOfflineDataSyncService {
  private offlineDataSyncErrorObjectList: Array<OfflineDataSyncErrorObject> = [];

  private unUploadInspData: UploadManagementSchema[];

  private imagesWithUploadUrl: InspectionImageWithUploadUrl[] = [];

  constructor(
    private uploadManagementDbService: UploadManagementDbService,
    private loginUserServiceOffline: LoginUserOfflineService,
    private inspectionDbService: InspectionDbService,
    private offlineManagementService: OfflineManagementService,
    private individualReportDbService: IndividualReportDbService,
    private inspectionImageService: InspectionImageOnlineService,
    private inspectionImageDbService: InspectionImageDbService,
    private oscSpinnerService: SpinnerService,
    private dialog: MatDialog,
    private offlineUtilsService: OfflineUtilsService,
    private util: UtilsService,
    private translate: TranslateService,
    private fileSysAccessService: FileSystemAccessService,
    private snackBarService: CommonSnackBarService,
    private progressDialogService: ProgressDialogService,

  ) { }

  private get tenantId() {
    return this.loginUserServiceOffline.tenantId;
  }

  private get userId() {
    return this.loginUserServiceOffline.loginUser?.id;
  }

  public async syncOfflineData() {
    this.offlineDataSyncErrorObjectList = [];
    return await this.uploadManagementDbService
      .getAll(this.tenantId, this.userId, null, null, false)
      .toPromise()
      .then(unUploadInspData => {
        this.unUploadInspData = unUploadInspData;
        const uploadInspIds = this.getUnUploadInspectionIds();

        if (uploadInspIds.length < 1) {
          return true;
        }

        this.openProgressDialog(uploadInspIds?.length);

        // 検査情報・検査画像情報・検査画像ファイルのアップロード
        return this.uploadOfflineData(uploadInspIds)
          .toPromise()
          .then(() => {
            if (this.offlineDataSyncErrorObjectList.length !== 0) {
              this.dialog.closeAll();
              this.snackBarService.open(this.translate.instant("dataUploadDialog.failedPost"));

              // エラーダイアログを表示する
              this.dialog.open(DataSyncAlertDialogComponent, {
                width: '600px',
                maxHeight: '485px',
                minHeight: '385px',
                disableClose: true,
                data: { offlineDataSyncErrorObjectList: this.offlineDataSyncErrorObjectList }
              });
              return false;
            }

            this.dialog.closeAll();
            this.snackBarService.open(this.translate.instant("dataUploadDialog.uploadSuccessful"));
            return true;
          });
      })
      .catch(error => {
        console.log(error);
        this.dialog.closeAll();
        this.snackBarService.open(this.translate.instant("dataUploadDialog.failedPost"));
        return false;
      });
  }

  private openProgressDialog(syncStepCount: number): MatDialogRef<ProgressDialogComponent> {
    this.oscSpinnerService.hide(true);
    return this.dialog.open(ProgressDialogComponent, {
      width: '600px',
      maxHeight: '700px',
      minHeight: '385px',
      disableClose: true,
      data: {
        syncStepCount: syncStepCount
      }
    });
  }

  private getUnUploadInspectionIds() {
    if (!this.unUploadInspData || this.unUploadInspData.length < 1) {
      return [];
    }

    return this.unUploadInspData
      .filter(data => data.type === UploadDataType.Inspection)
      .filter(data => !(data.isOfflineData && data.isDeleted))
      .filter(data => data.tenantId === this.tenantId)
      .map(data => data.dataId);
  }

  private uploadOfflineData(inspectionIds: string[]) {

    return this.inspectionDbService.getListById(inspectionIds)
      .pipe(switchMap(inspectionSchemas => {
        if (inspectionSchemas.length < 1) {
          return of([] as []);
        }
        return this.postOfflineInspections(inspectionSchemas);
      }));
  }

  private postOfflineInspections(inspectionSchemas: InspectionSchema[]) {
    let imageInfoSchemas: InspectionImageSchema[] = [];
    let imageFileSchemas: InspectionImageSchema[] = [];
    let individualReportSchemas: IndividualReportSchema[] = [];
    // Set the middle item of the Progress bar for each inspection data.
    const obs = inspectionSchemas.map((insp) => {
      const reportIds = this.getUnUploadIndividualReportIds(insp.id);
      return this.postOfflineInspection(insp)
        .pipe(switchMap(() => {
          // Get inspection images data and individual reports data associated with a single inspection data to be synchronized from SQLite.
          return zip(
            this.inspectionImageDbService.getList(this.getUnUploadImageInfoIds(insp.id), false),
            this.inspectionImageDbService.getList(this.getUnUploadImageFileIds(insp.id), false),
            this.individualReportDbService.getByIds(reportIds)
          );
        }))
        .pipe(switchMap(values => {
          imageInfoSchemas = values[0];
          imageFileSchemas = values[1];
          individualReportSchemas = values[2];
          if (!imageInfoSchemas || imageInfoSchemas.length < 1) {
            return of([] as UploadManagementSchema[]);
          }

          let modifiedImageCount = 0;
          imageFileSchemas.map(value => value?.modifiedItems ? modifiedImageCount += 1 : modifiedImageCount += 0);
          const measurementImageCount: number = imageFileSchemas.filter(value => value?.measurementItems).length;
          // Set the middle item of the Progress bar for each inspection data.
          this.progressDialogService.updateTotalDataCount(
            {
              totalDataCount: imageInfoSchemas.length * 4 + modifiedImageCount * 2 + measurementImageCount * 2 + individualReportSchemas.length,
              syncStep: DATA_SYNC_STEP.UPLOAD_INSPECTION,
              dataTitle: insp.title,
              descriptionList: [
                {
                  syncType: DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE,
                  totalCount: imageInfoSchemas.length * 2 + modifiedImageCount * 2 + measurementImageCount * 2
                },
                {
                  syncType: DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_INFO,
                  totalCount: imageInfoSchemas.length
                },
                {
                  syncType: DATA_SYNC_STEP.UPLOAD_REPORT_DATA,
                  totalCount: individualReportSchemas.length
                }
              ]
            });
          return this.postOfflineInspectionImage(imageInfoSchemas);
        }))
        .pipe(switchMap(() => this.postOfflineInspectionImageData(imageFileSchemas)))
        .pipe(switchMap(() => this.postOfflineInspectionImageSortOrder(insp.id, imageInfoSchemas)))
        .pipe(switchMap(() => this.postOfflineIndividualReport(individualReportSchemas)));
    });
    this.imagesWithUploadUrl = [];
    return this.util.chainObservables(obs, 1);
  }

  private getUnUploadIndividualReportIds(inspectionId: string) {
    if (!this.unUploadInspData || this.unUploadInspData.length < 1) {
      return [];
    }

    // 画像自体削除されていないもののみ
    return this.unUploadInspData
      .filter(data => data.parentDataId === inspectionId)
      .filter(data => data.type === UploadDataType.IndividualReport)
      .filter(data => !(data.isOfflineData && data.isDeleted))
      .filter(data => data.tenantId === this.tenantId)
      .map(data => data.dataId);
  }

  private postOfflineInspection(inspectionSchema: InspectionSchema) {
    inspectionSchema.sharedLockBy = this.userId;
    const request = new InspectionForOffline(inspectionSchema);

    return this.offlineManagementService.putOfflineInspection(request.id, request)
      .pipe(switchMap(inspection => {
        const schema = new InspectionSchemaUpdate();
        schema.setInspection(this.tenantId, inspection);
        return this.inspectionDbService.updateInspection(schema);
      }))
      .pipe(switchMap(() => this.uploadManagementDbService.uploaded(inspectionSchema.id, UploadDataType.Inspection)));
  }

  private getUnUploadImageInfoIds(inspectionId: string) {
    if (!this.unUploadInspData || this.unUploadInspData.length < 1) {
      return [];
    }

    // 検査画像情報はonlineDataなら全て, OfflineDataなら削除されていないもののみ
    return this.unUploadInspData
      .filter(data => data.parentDataId === inspectionId)
      .filter(data => data.type === UploadDataType.InspectionImageInfo)
      .filter(data => !(data.isOfflineData && data.isDeleted))
      .filter(data => data.tenantId === this.tenantId)
      .map(data => data.dataId);
  }

  private getUnUploadImageFileIds(inspectionId: string) {
    if (!this.unUploadInspData || this.unUploadInspData.length < 1) {
      return [];
    }

    // 画像自体削除されていないもののみ
    return this.unUploadInspData
      .filter(data => data.parentDataId === inspectionId)
      .filter(data => data.type === UploadDataType.InspectionImageFile)
      .filter(data => data.isDeleted === false)
      .filter(data => data.tenantId === this.tenantId)
      .map(data => data.dataId);
  }

  /** Upload image files edited online to online blob storage. */
  private postOfflineInspectionImage(imageSchemas: InspectionImageSchema[]) {
    if (!imageSchemas || imageSchemas.length < 1) {
      return of([] as UploadManagementSchema[]);
    }

    const obs = imageSchemas.map(imageSchema => {
      const imageInfo = new InspectionImageForOffline(imageSchema);
      imageInfo.sortOrder = null;
      return this.offlineManagementService.putOfflineInspectionImage(imageInfo.inspectionId, imageInfo.id, imageInfo)
        .pipe(
          switchMap(inspImage => {
            inspImage.audioFilePath = imageInfo.audioFilePath;
            this.imagesWithUploadUrl.push(inspImage);
            this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_INFO);
            // Overwrite existing offline data with data synchronized online.
            const schema = new InspectionImageBaseSchema();
            schema.setInspectionImage(inspImage, this.tenantId, this.userId);
            return this.inspectionImageDbService.put(schema);
          }),
          catchError(async () => {
            this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_INFO);
            this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
            return this.inspectionImageDbService.put(imageSchema);
          }))
        .pipe(
          switchMap(() => {
            return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionImageInfo);
          }));
    });
    return this.util.chainObservables(obs, 8);
  }

  /** Upload online edited image binary to online. */
  public postOfflineInspectionImageData(imageSchemas: InspectionImageSchema[]) {
    if (!imageSchemas || imageSchemas.length < 1 || !this.imagesWithUploadUrl || this.imagesWithUploadUrl.length < 1) {
      return of([] as UploadManagementSchema[]);
    }
    const obs = imageSchemas.map(imageSchema => {
      return zip(
        this.offlineUtilsService.getImageObj(imageSchema),
        this.offlineUtilsService.getModifiedImageObj(imageSchema),
        this.offlineUtilsService.getMeasurementImageObj(imageSchema),
        this.offlineUtilsService.getThumbnailImageObj(imageSchema),
        this.offlineUtilsService.getModifiedThumbnailImageObj(imageSchema),
        this.offlineUtilsService.getMeasurementThumbnailImageObj(imageSchema)
      )
        .pipe(
          switchMap(fileList => {
            const imageFile: File = fileList[0];
            const modifiedImageFile: File = fileList[1];
            const measurementImageFile: File = fileList[2];
            const thumbnailImageFile: File = fileList[3];
            const modifiedThumbnailImageFile: File = fileList[4];
            const measurementThumbnailImageFile: File = fileList[5];

            const inspImage = this.imagesWithUploadUrl.find(image => image.id == imageSchema.id);
            const imageOb = from(this.uploading(imageFile, inspImage.uploadUrl))
              .pipe(switchMap(response => {
                if (response === 201) {
                  this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                  // Upload画像の読み込み、更新
                  return this.readInspectionImage(imageSchema.inspectionId, imageSchema.id, ReadImageOption.Sync)
                    .pipe(switchMap(response => {
                      const schema = new InspectionImageBaseSchema();
                      if (response) {
                        schema.setInspectionImage(response, this.tenantId, this.userId);
                        return this.inspectionImageDbService.put(schema)
                          .pipe(switchMap(() => this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionImageFile)));
                      } else {
                        schema.setInspectionImage(inspImage, this.tenantId, this.userId);
                        return this.inspectionImageDbService.put(schema);
                      }
                    }));
                } else {
                  this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                  this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                  // Overwrite existing offline data with data synchronized online.
                  return this.inspectionImageDbService.put(imageSchema);
                }
              }));

            let modifiedImageOb = of(null as UploadManagementSchema);
            if (imageSchema?.modifiedItems) {
              modifiedImageOb = this.offlineManagementService
                .putOfflineInspectionModifiedImageBinary(imageSchema.inspectionId, imageSchema.id, modifiedImageFile, USE_IMAGE_TYPE.RETOUCHED)
                .pipe(
                  switchMap(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionModifiedImageFile);
                  }),
                  catchError(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                    return of(null as UploadManagementSchema);
                  })
                );
            }

            let measurementImageOb: Observable<UploadManagementSchema> = of(null);
            if (imageSchema?.measurementItems) {
              measurementImageOb = this.offlineManagementService
                .putOfflineInspectionModifiedImageBinary(imageSchema.inspectionId, imageSchema.id, measurementImageFile, USE_IMAGE_TYPE.REMEASUREMENT)
                .pipe(
                  switchMap(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionMeasurementImageFile);
                  }),
                  catchError(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                    return of(null as UploadManagementSchema);
                  })
                );
            }

            let thumbnailImageOb: Observable<UploadManagementSchema> = of(null);
            if (thumbnailImageFile && thumbnailImageFile.size > 0) {
              thumbnailImageOb = this.offlineManagementService
                .putOfflineInspectionModifiedImageBinary(imageSchema.inspectionId, imageSchema.id, thumbnailImageFile, USE_IMAGE_TYPE.ORIGINAL_THUMBNAIL)
                .pipe(
                  switchMap(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionThumbnailImageFile);
                  }),
                  catchError(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                    return of(null as UploadManagementSchema);
                  })
                );
            }

            let modifiedThumbnailImageOb: Observable<UploadManagementSchema> = of(null);
            if (imageSchema?.modifiedItems) {
              modifiedThumbnailImageOb = this.offlineManagementService
                .putOfflineInspectionModifiedImageBinary(imageSchema.inspectionId, imageSchema.id, modifiedThumbnailImageFile, USE_IMAGE_TYPE.RETOUCHED_THUMBNAIL)
                .pipe(
                  switchMap(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionModifiedThumbnailImageFile);
                  }),
                  catchError(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                    return of(null as UploadManagementSchema);
                  })
                );
            }

            let measurementThumbnailImageOb: Observable<UploadManagementSchema> = of(null);
            if (imageSchema?.measurementItems) {
              measurementThumbnailImageOb = this.offlineManagementService
                .putOfflineInspectionModifiedImageBinary(imageSchema.inspectionId, imageSchema.id, measurementThumbnailImageFile, USE_IMAGE_TYPE.REMEASUREMENT_THUMBNAIL)
                .pipe(
                  switchMap(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionMeasurementThumbnailImageFile);
                  }),
                  catchError(() => {
                    this.progressDialogService.updateProgressStep(DATA_SYNC_STEP.UPLOAD_INSPECTION_IMAGE_FILE);
                    this.setDataSyncErrorObject(imageSchema.inspectionId, imageSchema, null);
                    return of(null as UploadManagementSchema);
                  })
                );
            }

            let audioOb: Observable<UploadManagementSchema> = of(null);
            if (inspImage.uploadAudioUrl) {
              const uploadAudio = async () => {
                const fullPath = this.fileSysAccessService.getFileFullPath(inspImage.audioFilePath);
                const wav: File = await this.util.getFileFromUrl(await fullPath, imageSchema.fileName);

                return this.uploading(wav, inspImage.uploadAudioUrl);
              };

              audioOb = from(uploadAudio())
                .pipe(
                  switchMap(response => {
                    if (response === 201) {
                      return this.uploadManagementDbService.uploaded(imageSchema.id, UploadDataType.InspectionMeasurementImageAudioFile);
                    } else {
                      return of(null as UploadManagementSchema);
                    }
                  })
                );
            }
            return zip(imageOb, modifiedImageOb, measurementImageOb, thumbnailImageOb, modifiedThumbnailImageOb, measurementThumbnailImageOb, audioOb);
          }));
    });
    return this.util.chainObservables(obs, 8);
  }

  private postOfflineIndividualReport(individualReportSchema: IndividualReportSchema[]) {
    if (individualReportSchema?.length < 1) {
      return of([] as []);
    }

    const measurementModeName = new MeasurementModeName(this.translate);

    const obs = individualReportSchema.map(report => {
      const reportInfo = new IndividualReportForOffline(report);
      const reportSchema = report as IndividualReportSchema;
      return this.offlineManagementService.putOfflineIndividual(reportInfo.reportId, reportInfo, measurementModeName)
        .pipe(
          switchMap(inspectionReport => {
            // Overwrite existing offline data with data synchronized online.
            return this.individualReportDbService.put(reportSchema)
              .pipe(switchMap(() => this.uploadManagementDbService.uploaded(reportSchema.id, UploadDataType.IndividualReport)));
          }),
          catchError(() => {
            this.setDataSyncErrorObject(reportSchema.inspectionId, null, reportSchema);
            return this.individualReportDbService.put(reportSchema);
          })
        );
    });
    return this.util.chainObservables(obs, 8);
  }

  public postOfflineInspectionImageSortOrder(inspectionId: string, imageSchemas: InspectionImageSchema[]) {
    const locationOrder: LocationOrder[] = [];

    imageSchemas.forEach(imageSchema => {
      if (!locationOrder || !locationOrder.some((_: LocationOrder) => _.subjectLocationId === imageSchema.subjectLocationId)) {
        const imagesSortOrder = new ImagesSortOrder(imageSchema.id, imageSchema.sortOrder);
        locationOrder.push(new LocationOrder(imageSchema.subjectLocationId, [imagesSortOrder]));
      } else {
        locationOrder.find((_: LocationOrder) => _.subjectLocationId === imageSchema.subjectLocationId).imagesSortOrder
          .push(new ImagesSortOrder(imageSchema.id, imageSchema.sortOrder));
      }
    });

    const inspectionImagesSortOrder = new InspectionImagesSortOrderForOffline(inspectionId, locationOrder);
    return this.offlineManagementService.putOfflineInspectionImagesOrder(inspectionId, inspectionImagesSortOrder)
      .pipe(switchMap(() => this.uploadManagementDbService.uploaded(inspectionId, UploadDataType.InspectionImageInfo)));
  }

  private async setDataSyncErrorObject(inspectionId: string, inspectionImageSchema: InspectionImageSchema, reportSchema: IndividualReportSchema) {
    const errorObj = new OfflineDataSyncErrorObject();
    const inspectionSchema = await this.inspectionDbService.getInspection(inspectionImageSchema.inspectionId, true).toPromise();
    errorObj.inspectionId = inspectionImageSchema.inspectionId;
    errorObj.inspectionSchema = inspectionSchema;

    if (this.offlineDataSyncErrorObjectList.filter(_ => _.inspectionId == inspectionId).length === 0) {
      if (inspectionImageSchema) {
        errorObj.inspectionImageSchemaList.push(inspectionImageSchema);
      }
      if (reportSchema) {
        errorObj.individualReportSchemaList.push(reportSchema);
      }
      this.offlineDataSyncErrorObjectList.push(errorObj);
    } else {
      if (inspectionImageSchema) {
        this.offlineDataSyncErrorObjectList.filter(_ => _.inspectionId === inspectionId)[0].inspectionImageSchemaList.push(inspectionImageSchema);
      }
      if (reportSchema) {
        this.offlineDataSyncErrorObjectList.filter(_ => _.inspectionId === reportSchema.inspectionId)[0].individualReportSchemaList.push(reportSchema);
      }
    }
  }

  private readInspectionImage(inspectionId: string, imageId: string, readImageOption: ReadImageOption, callCount = 0): Observable<InspectionImage> {
    return this.inspectionImageService.readInspectionImage(inspectionId, imageId, readImageOption)
      .pipe(catchError((error: HttpErrorResponse) => {
        if (callCount >= 5) {
          return of(null as InspectionImage);
        }
        callCount++;
        return this.readInspectionImage(inspectionId, imageId, readImageOption, callCount);
      }));
  }

  async uploading(file: File | Blob, uploadUrl: string): Promise<number> {
    try {
      const blobClient = BlobClient.create(uploadUrl);

      const response = await blobClient.uploadData(file, {
        blockSize: 4 * 1024 * 1024, // 4MB
        maxSingleShotSize: 4 * 1024 * 1024,
        concurrency: 20, // 20 uploads at the same time
        blobHTTPHeaders: { blobContentType: file.type }
      });

      return response._response.status;
    } catch (e) {
      return 500;
    }
  }

}
